// Translates OLD-schema (development / main) decoded monitoring values into the NEW
// (config_wizard / flattened-valued-messages) types the shared monitoring UI renders.
//
// The flatten kept enum value NAMES but reordered/renumbered their integers and (for
// service/fault) inserted a Normal/Valued variant level. So the authoritative mapping is
// BY NAME, read straight off the generated enums (bidirectional `export enum`s) — no
// hand-maintained tables. OLD values whose name has no NEW counterpart are dropped.

import { Control as NewControl } from "../generated/control/control";
import { DeviceServiceContent as NewDeviceServiceContent } from "../generated/service/device-service-content";
import { ElevatorFaultMessageT as NewElevatorFaultMessageT } from "../generated/fault/elevator-fault-message";
import { ElevatorServiceMessageT as NewElevatorServiceMessageT } from "../generated/service/elevator-service-message";
import { EscalatorFaultMessageT as NewEscalatorFaultMessageT } from "../generated/fault/escalator-fault-message";
import { EscalatorServiceMessageT as NewEscalatorServiceMessageT } from "../generated/service/escalator-service-message";
import { Fault as NewFault } from "../generated/fault/fault";
import { FaultContent as NewFaultContent } from "../generated/fault/fault-content";
import { FaultMessageT as NewFaultMessageT } from "../generated/fault/fault-message";
import { FaultVariant as NewFaultVariant } from "../generated/fault/fault-variant";
import { NormalFaultT as NewNormalFaultT } from "../generated/fault/normal-fault";
import { NormalServiceT as NewNormalServiceT } from "../generated/service/normal-service";
import { Service as NewService } from "../generated/service/service";
import { ServiceMessageT as NewServiceMessageT } from "../generated/service/service-message";
import { ServiceVariant as NewServiceVariant } from "../generated/service/service-variant";
import { Usage as NewUsage } from "../generated/usage/usage";
import { MessageContent as NewMessageContent } from "../generated/ln2/message-content";

import { DeviceServiceContent as OldDeviceServiceContent } from "../generated_legacy/service/device-service-content";
import { ElevatorControl as OldElevatorControl } from "../generated_legacy/control/elevator-control";
import { ElevatorFault as OldElevatorFault } from "../generated_legacy/fault/elevator-fault";
import { ElevatorFaultMessageT as OldElevatorFaultMessageT } from "../generated_legacy/fault/elevator-fault-message";
import { ElevatorService as OldElevatorService } from "../generated_legacy/service/elevator-service";
import { ElevatorServiceMessageT as OldElevatorServiceMessageT } from "../generated_legacy/service/elevator-service-message";
import { ElevatorUsage as OldElevatorUsage } from "../generated_legacy/usage/elevator-usage";
import { EscalatorControl as OldEscalatorControl } from "../generated_legacy/control/escalator-control";
import { EscalatorFault as OldEscalatorFault } from "../generated_legacy/fault/escalator-fault";
import { EscalatorFaultMessageT as OldEscalatorFaultMessageT } from "../generated_legacy/fault/escalator-fault-message";
import { EscalatorService as OldEscalatorService } from "../generated_legacy/service/escalator-service";
import { EscalatorServiceMessageT as OldEscalatorServiceMessageT } from "../generated_legacy/service/escalator-service-message";
import { EscalatorUsage as OldEscalatorUsage } from "../generated_legacy/usage/escalator-usage";
import { FaultContent as OldFaultContent } from "../generated_legacy/fault/fault-content";
import { FaultMessageT as OldFaultMessageT } from "../generated_legacy/fault/fault-message";
import { ServiceMessageT as OldServiceMessageT } from "../generated_legacy/service/service-message";
import { OneHotMessageContent as OldOneHotMessageContent } from "../generated_legacy/ln2/one-hot-message-content";

type EnumLike = Record<string, string | number>;

// Forward-look a NEW enum value by name (numeric enums also carry reverse entries, so
// guard on `typeof === number`). Returns undefined when the name has no NEW counterpart.
function mapByName(name: string | undefined, newEnum: EnumLike): number | undefined {
  if (name === undefined) {
    return undefined;
  }

  const value = newEnum[name];

  return typeof value === "number" ? value : undefined;
}

// Reverse-look a numeric value's name in whichever OLD enum defines it.
function oldName(value: number, ...oldEnums: EnumLike[]): string | undefined {
  const hit = oldEnums.map((e) => e[value]).find((name) => typeof name === "string");

  return typeof hit === "string" ? hit : undefined;
}

// Usage value (direction / door-zone). OLD ElevatorUsage/EscalatorUsage value -> NEW Usage.
/**
 * Maps a legacy usage code to the current `Usage` enum, or null when unset.
 */
export function translateUsageValue(value: number | null | undefined): NewUsage | null {
  if (value === null || value === undefined) {
    return null;
  }

  const mapped = mapByName(
    oldName(value, OldElevatorUsage, OldEscalatorUsage),
    NewUsage as EnumLike,
  );

  return mapped ?? null;
}

// OneHot category (union discriminant). OLD OneHotMessageContent value -> NEW MessageContent.
// Keeps the legacy oneHotMap keyed by the SAME category integers the NEW applyDeltaToState path
// uses (NEW Usage_UsageMessage=3 / Service_ServiceMessage=4, not OLD 1 / 2), so a consumer that
// queries by a NEW MessageContent value resolves OLD-edge entries too. Returns null when the OLD
// category name has no NEW counterpart.
/**
 * Maps a legacy one-hot category code to its current enum value.
 */
export function translateOneHotCategory(
  value: number | null | undefined,
): NewMessageContent | null {
  if (value === null || value === undefined) {
    return null;
  }

  const mapped = mapByName(oldName(value, OldOneHotMessageContent), NewMessageContent as EnumLike);

  return mapped ?? null;
}

// Control value (escalatorControl). OLD Elevator/EscalatorControl value -> NEW Control.
/**
 * Maps a legacy control code to the current `Control` enum, or null when unset.
 */
export function translateControlValue(value: number | null | undefined): NewControl | null {
  if (value === null || value === undefined) {
    return null;
  }

  const mapped = mapByName(
    oldName(value, OldElevatorControl, OldEscalatorControl),
    NewControl as EnumLike,
  );

  return mapped ?? null;
}

// OLD ServiceMessage (messageType + Elevator/EscalatorServiceMessage.message: ElevatorService)
// -> NEW ServiceMessage (messageType + Elevator/EscalatorServiceMessage(ServiceVariant.NormalService,
// NormalService(Service))). Returns null when the service value has no NEW counterpart.
/**
 * Converts a legacy service message to the current schema, or null when there is nothing to convert.
 */
export function translateService(old: OldServiceMessageT | null): NewServiceMessageT | null {
  if (!old || !old.message) {
    return null;
  }

  const isElevator = old.messageType === OldDeviceServiceContent.ElevatorServiceMessage;
  const oldValue = (old.message as OldElevatorServiceMessageT | OldEscalatorServiceMessageT)
    .message;
  const newValue = mapByName(
    oldName(oldValue, isElevator ? OldElevatorService : OldEscalatorService),
    NewService as EnumLike,
  );

  if (newValue === undefined) {
    return null;
  }

  const normal = new NewNormalServiceT(newValue);
  const inner = isElevator
    ? new NewElevatorServiceMessageT(NewServiceVariant.NormalService, normal)
    : new NewEscalatorServiceMessageT(NewServiceVariant.NormalService, normal);

  return new NewServiceMessageT(
    isElevator
      ? NewDeviceServiceContent.ElevatorServiceMessage
      : NewDeviceServiceContent.EscalatorServiceMessage,
    inner,
  );
}

// OLD FaultMessage -> NEW FaultMessage (same Normal-variant restructure as service).
/**
 * Converts a legacy fault message to the current schema, or null when there is nothing to convert.
 */
export function translateFault(old: OldFaultMessageT | null): NewFaultMessageT | null {
  if (!old || !old.fault) {
    return null;
  }

  const isElevator = old.faultType === OldFaultContent.ElevatorFaultMessage;
  const oldValue = (old.fault as OldElevatorFaultMessageT | OldEscalatorFaultMessageT).message;
  const newValue = mapByName(
    oldName(oldValue, isElevator ? OldElevatorFault : OldEscalatorFault),
    NewFault as EnumLike,
  );

  if (newValue === undefined) {
    return null;
  }

  const normal = new NewNormalFaultT(newValue);
  const inner = isElevator
    ? new NewElevatorFaultMessageT(NewFaultVariant.NormalFault, normal)
    : new NewEscalatorFaultMessageT(NewFaultVariant.NormalFault, normal);

  return new NewFaultMessageT(
    isElevator ? NewFaultContent.ElevatorFaultMessage : NewFaultContent.EscalatorFaultMessage,
    inner,
  );
}

// Translate an OLD list of faults, dropping any with no NEW counterpart.
/**
 * Converts a list of legacy faults, dropping the entries that translate to nothing.
 */
export function translateFaults(faults: (OldFaultMessageT | null)[]): NewFaultMessageT[] {
  return faults.map((f) => translateFault(f)).filter((f): f is NewFaultMessageT => f !== null);
}
